import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import type { HourlyPackage, PackageFilters } from '../types';

interface HourlyPackagesDataTableProps {
  data: HourlyPackage[];
  columns: ColumnDef<HourlyPackage>[];
  isLoading: boolean;
  filters: PackageFilters;
  updateFilter: <K extends keyof PackageFilters>(key: K, value: PackageFilters[K]) => void;
  updateFilters?: (updates: Partial<PackageFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

export function HourlyPackagesDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: HourlyPackagesDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('hourly-packages');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
  } = filters;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters['createdAt.min'] || filters['createdAt.max']) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Toolbar: status pills + table controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-2">
        <StatusFilterPills
          value={filters.status as string | undefined}
          onChange={(v) => updateFilter('status', v)}
          options={[
            { value: 'ACTIVE', label: t('status.ACTIVE'), activeClassName: 'data-[state=on]:bg-green-600' },
            { value: 'INACTIVE', label: t('status.INACTIVE'), activeClassName: 'data-[state=on]:bg-gray-400' },
          ]}
        />
        <div className="flex items-center gap-2">
          {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={3}
      >
        <FilterField label={t('createdDateRange')}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
              >
                <span className="truncate">
                  {minDate && maxDate
                    ? `${format(new Date(minDate), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(maxDate), 'MM/dd/yy', { locale: dateFnsLocale })}`
                    : t('all')}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="range"
                selected={{
                  from: minDate ? new Date(minDate) : undefined,
                  to: maxDate ? new Date(maxDate) : undefined,
                }}
                onSelect={(range) => {
                  if (updateFilters) {
                    updateFilters({
                      'createdAt.min': range?.from?.toISOString(),
                      'createdAt.max': range?.to?.toISOString(),
                    });
                  } else {
                    updateFilter('createdAt.min', range?.from?.toISOString());
                    updateFilter('createdAt.max', range?.to?.toISOString());
                  }
                }}
                numberOfMonths={2}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </FilterField>

      </CollapsibleFilters>

      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noHourlyPackagesFound')}</h3>
                    <p className="text-[#64748b]">{t('trackHourlyPackagesMessage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
